import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Room, RoomEvent, Track } from 'livekit-client';
import type { RemoteVideoTrack, RemoteAudioTrack } from 'livekit-client';
import { GreenroomState as GS } from '../../stores/types/types';
import { GetLivekitViewerToken } from '../../connection/livekit';

export interface LivekitParticipant {
	videoTrack: RemoteVideoTrack | null;
	audioTrack: RemoteAudioTrack | null;
}

interface LivekitContextValue {
	participantsMap: Map<string, LivekitParticipant>;
}

const LivekitContext = createContext<LivekitContextValue>({ participantsMap: new Map() });

export function LivekitProvider({ children }: { children: React.ReactNode }) {
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meetingId = useSelector((s: GS) => s.SessionReducer.meeting?.meeting);
	const ingressSessions = useSelector((s: GS) => s.LivekitReducer.livekitIngresses);

	const [participantsMap, setParticipantsMap] = useState<Map<string, LivekitParticipant>>(new Map());
	const roomRef = useRef<Room | null>(null);
	const hasSessionsRef = useRef(false);

	useEffect(() => {
		const hasSessions = ingressSessions.length > 0;

		if (!hasSessions) {
			if (roomRef.current) {
				roomRef.current.disconnect();
				roomRef.current = null;
				setParticipantsMap(new Map());
			}
			hasSessionsRef.current = false;
			return;
		}

		// Already connected — new sessions added to the same room, no reconnect needed
		if (roomRef.current && hasSessionsRef.current) return;

		if (!token || !meetingId) return;
		hasSessionsRef.current = true;
		let active = true;

		GetLivekitViewerToken(token, meetingId).then((result) => {
			if (!active || !result?.token || !result?.serverUrl || roomRef.current) return;

			const room = new Room();
			roomRef.current = room;

			room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
				setParticipantsMap((prev) => {
					const next = new Map(prev);
					const existing = next.get(participant.identity) ?? { videoTrack: null, audioTrack: null };
					if (track.kind === Track.Kind.Video) {
						next.set(participant.identity, { ...existing, videoTrack: track as RemoteVideoTrack });
					} else if (track.kind === Track.Kind.Audio) {
						next.set(participant.identity, { ...existing, audioTrack: track as RemoteAudioTrack });
					}
					return next;
				});
			});

			room.on(RoomEvent.TrackUnsubscribed, (track, _pub, participant) => {
				setParticipantsMap((prev) => {
					const next = new Map(prev);
					const existing = next.get(participant.identity);
					if (!existing) return prev;

					const updated: LivekitParticipant = {
						videoTrack: track.kind === Track.Kind.Video ? null : existing.videoTrack,
						audioTrack: track.kind === Track.Kind.Audio ? null : existing.audioTrack,
					};

					if (!updated.videoTrack && !updated.audioTrack) {
						next.delete(participant.identity);
					} else {
						next.set(participant.identity, updated);
					}
					return next;
				});
			});

			room.on(RoomEvent.Disconnected, () => {
				setParticipantsMap(new Map());
				roomRef.current = null;
				hasSessionsRef.current = false;
			});

			room.connect(
				result.serverUrl,
				result.token,
				{ rtcConfig: { iceTransportPolicy: 'relay' }}
			).catch((err) => {
				console.error('[livekit-room] connect error:', err);
			});
		});

		return () => {
			active = false;
			roomRef.current?.disconnect();
			roomRef.current = null;
			hasSessionsRef.current = false;
		};
	}, [token, meetingId, ingressSessions.length]);

	return (
		<LivekitContext.Provider value={{ participantsMap }}>
			{children}
		</LivekitContext.Provider>
	);
}

export function useLivekit(): LivekitContextValue {
	return useContext(LivekitContext);
}
